/* *
 * AscendDraft Logo Component
 * ==========================
 * Centralized logo component for consistent branding across all pages.
 * 
 * Usage:
 *   import { Logo, renderLogo } from './components/logo.js';
 *   
 *   // In HTML: <div id="logo-container"></div>
 *   Logo.render('#logo-container', { size: 'md', variant: 'full' });
 *   
 *   // Or get HTML string:
 *   const logoHtml = renderLogo({ size: 'lg', variant: 'icon' });
 *   
 *   // Use HD image logo:
 *   const hdLogoHtml = renderLogo({ size: 'lg', variant: 'image' });
 * 
 * @version 1.1.0 */
'use strict';

// ========================================================================
// LOGO CONFIGURATION
// ========================================================================

const LOGO_COLORS = {
    cyan: '#00BCD4',
    gray: '#9CA3AF',
    white: '#FFFFFF',
    background: '#000000',
    gradient: {
      start: '#00BCD4',
      end: '#00ACC1'
    }
  };

  const LOGO_SIZES = {
    xs: { height: 24, fontSize: '0.875rem' },
    sm: { height: 32, fontSize: '1rem' },
    md: { height: 40, fontSize: '1.25rem' },
    lg: { height: 56, fontSize: '1.5rem' },
    xl: { height: 80, fontSize: '2rem' }
  };

  // ========================================================================
  // PATH RESOLUTION FOR LOGO IMAGE
  // ========================================================================
  
  /* *
   * Resolve the path to the HD logo image based on current page location
   * The logo is stored at: public/logo-hd.png (relative to workspace root)
   * @returns {string} - Resolved path to logo-hd.png */
  function getLogoImagePath() {
    const path = window.location.pathname;
    
    // Determine base path for the workspace
    const isInAscendDraftWebsite = path.includes('/AscendDraft-Website/');
    const basePath = isInAscendDraftWebsite ? '/AscendDraft-Website' : '';
    
    // Calculate relative path based on current page depth
    // The logo is at: {workspace}/public/logo-hd.png
    // From AscendDraft-Website folder, it's at: ../public/logo-hd.png
    
    if (isInAscendDraftWebsite) {
      // Count directory depth from AscendDraft-Website root
      const afterBase = path.split('/AscendDraft-Website/')[1] || '';
      const depth = afterBase.split('/').filter(p => p && !p.includes('.')).length;
      
      // Build relative path going up from current page
      const upPath = '../'.repeat(depth + 1); // +1 to go above AscendDraft-Website
      return upPath + 'public/logo-hd.png';
    }
    
    // Default: assume we're at workspace root level
    return '/public/logo-hd.png';
  }

  /* *
   * Get the logo image path with fallback resolution
   * @param {string} customPath - Optional custom path override
   * @returns {string} - Resolved logo image path */
  function resolveLogoPath(customPath) {
    if (customPath) return customPath;
    return getLogoImagePath();
  }

  // ========================================================================
  // SVG LOGO MARKUP
  // ========================================================================

  /* *
   * Get inline SVG for the "A" icon mark
   * @param {number} size - Size in pixels
   * @returns {string} - SVG markup */
  function getIconSvg(size = 40) {
    return `
      <svg class="logo-icon" width="${size}" height="${size}" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="logo-icon-gradient" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stop-color="${LOGO_COLORS.cyan}"/>
            <stop offset="100%" stop-color="${LOGO_COLORS.gradient.end}"/>
          </linearGradient>
        </defs>
        <rect width="40" height="40" rx="8" fill="#0d1117"/>
        <path d="M12 28L20 12L28 28" stroke="url(#logo-icon-gradient)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M15 23H25" stroke="url(#logo-icon-gradient)" stroke-width="2.5" stroke-linecap="round"/>
      </svg>
    `;
  }

  /* *
   * Get full logo with icon and text
   * @param {Object} options - Logo options
   * @returns {string} - HTML markup */
  function getFullLogo(options = {}) {
    const {
      size = 'md',
      href = '/',
      showText = true,
      className = '',
      ariaLabel = 'AscendDraft Home'
    } = options;

    const sizeConfig = LOGO_SIZES[size] || LOGO_SIZES.md;
    const iconSize = sizeConfig.height;
    
    const logoContent = `
      ${getIconSvg(iconSize)}
      ${showText ? `
        <span class="logo-wordmark" style="font-size: ${sizeConfig.fontSize};">
          <span class="logo-text-ascend" style="color: ${LOGO_COLORS.gray};">ASCEND</span><span class="logo-text-draft" style="color: ${LOGO_COLORS.white};">DRAFT</span>
        </span>
      ` : ''}
    `;

    if (href) {
      return `
        <a href="${href}" class="logo ${className}" aria-label="${ariaLabel}">
          ${logoContent}
        </a>
      `;
    }

    return `
      <div class="logo ${className}">
        ${logoContent}
      </div>
    `;
  }

  /* *
   * Get icon-only logo (for collapsed sidebars, favicons, etc.)
   * @param {Object} options - Logo options
   * @returns {string} - HTML markup */
  function getIconLogo(options = {}) {
    const {
      size = 'md',
      href = '/',
      className = '',
      ariaLabel = 'AscendDraft'
    } = options;

    const sizeConfig = LOGO_SIZES[size] || LOGO_SIZES.md;
    const iconSize = sizeConfig.height;

    if (href) {
      return `
        <a href="${href}" class="logo logo-icon-only ${className}" aria-label="${ariaLabel}">
          ${getIconSvg(iconSize)}
        </a>
      `;
    }

    return `
      <div class="logo logo-icon-only ${className}">
        ${getIconSvg(iconSize)}
      </div>
    `;
  }

  /* *
   * Get text-only logo (cyan A + gray SCEND + white DRAFT)
   * @param {Object} options - Logo options
   * @returns {string} - HTML markup */
  function getTextLogo(options = {}) {
    const {
      size = 'md',
      href = '/',
      className = '',
      ariaLabel = 'AscendDraft Home'
    } = options;

    const sizeConfig = LOGO_SIZES[size] || LOGO_SIZES.md;

    const logoText = `
      <span class="logo-text-full" style="font-size: ${sizeConfig.fontSize}; font-weight: 700; letter-spacing: -0.5px;">
        <span style="color: ${LOGO_COLORS.cyan};">A</span><span style="color: ${LOGO_COLORS.gray};">SCEND</span><span style="color: ${LOGO_COLORS.white};">DRAFT</span>
      </span>
    `;

    if (href) {
      return `
        <a href="${href}" class="logo logo-text-only ${className}" aria-label="${ariaLabel}">
          ${logoText}
        </a>
      `;
    }

    return `
      <div class="logo logo-text-only ${className}">
        ${logoText}
      </div>
    `;
  }

  /* *
   * Get HD image logo (uses public/logo-hd.png)
   * This is the PRIMARY logo variant for navigation headers
   * @param {Object} options - Logo options
   * @returns {string} - HTML markup */
  function getImageLogo(options = {}) {
    const {
      size = 'md',
      href = '/',
      className = '',
      ariaLabel = 'AscendDraft Home',
      imagePath = null
    } = options;

    const sizeConfig = LOGO_SIZES[size] || LOGO_SIZES.md;
    const logoSrc = resolveLogoPath(imagePath);
    const height = sizeConfig.height;
    
    // Calculate approximate width based on typical logo aspect ratio (around 3:1 for full logo)
    const width = height * 3;

    const logoContent = `
      <img 
        src="${logoSrc}" 
        alt="AscendDraft" 
        class="logo-image" 
        height="${height}"
        style="height: ${height}px; width: auto; max-width: ${width}px; object-fit: contain;"
        onerror="this.onerror=null; this.style.display='none'; this.parentElement.innerHTML += '${getTextLogoFallback(sizeConfig)}';"
      >
    `;

    if (href) {
      return `
        <a href="${href}" class="logo logo-image-container ${className}" aria-label="${ariaLabel}" style="display: inline-flex; align-items: center; text-decoration: none;">
          ${logoContent}
        </a>
      `;
    }

    return `
      <div class="logo logo-image-container ${className}" style="display: inline-flex; align-items: center;">
        ${logoContent}
      </div>
    `;
  }

  /* *
   * Get text logo as fallback (escaped for inline use)
   * @param {Object} sizeConfig - Size configuration
   * @returns {string} - Escaped HTML for fallback */
  function getTextLogoFallback(sizeConfig) {
    return `<span class="logo-wordmark" style="font-size: ${sizeConfig.fontSize}; font-weight: 700;"><span style="color: ${LOGO_COLORS.gray};">ASCEND</span><span style="color: ${LOGO_COLORS.white};">DRAFT</span></span>`;
  }

  // ========================================================================
  // PUBLIC API
  // ========================================================================

  const Logo = {
    /* *
     * Render logo into a container
     * @param {string|Element} container - Selector or element
     * @param {Object} options - Logo options */
    render(container, options = {}) {
      const el = typeof container === 'string' 
        ? document.querySelector(container) 
        : container;
      
      if (!el) {
        console.warn('[Logo] Container not found:', container);
        return;
      }

      const variant = options.variant || 'full';
      let html;

      switch (variant) {
        case 'icon':
          html = getIconLogo(options);
          break;
        case 'text':
          html = getTextLogo(options);
          break;
        case 'image':
          html = getImageLogo(options);
          break;
        case 'full':
        default:
          html = getFullLogo(options);
          break;
      }

      el.innerHTML = html;
    },

    /* *
     * Get logo HTML without rendering
     * @param {Object} options - Logo options
     * @returns {string} - HTML markup */
    getHtml(options = {}) {
      const variant = options.variant || 'full';
      
      switch (variant) {
        case 'icon':
          return getIconLogo(options);
        case 'text':
          return getTextLogo(options);
        case 'image':
          return getImageLogo(options);
        case 'full':
        default:
          return getFullLogo(options);
      }
    },

    /* *
     * Get the resolved path to the HD logo image
     * @returns {string} - Logo image path */
    getImagePath() {
      return getLogoImagePath();
    },

    // Expose configuration
    colors: LOGO_COLORS,
    sizes: LOGO_SIZES
  };

  /* *
   * Shorthand for getting logo HTML
   * @param {Object} options - Logo options
   * @returns {string} - HTML markup */
  function renderLogo(options = {}) {
    return Logo.getHtml(options);
  }

  // ========================================================================
  // CSS INJECTION
  // ========================================================================

  function injectLogoStyles() {
    if (document.getElementById('ascend-logo-styles')) return;

    const styles = document.createElement('style');
    styles.id = 'ascend-logo-styles';
    styles.textContent = `
      /* AscendDraft Logo Component Styles */
      .logo {
        display: inline-flex;
        align-items: center;
        gap: 10px;
        text-decoration: none;
        transition: opacity 0.2s ease;
      }
      
      .logo:hover {
        opacity: 0.9;
      }
      
      .logo-icon {
        flex-shrink: 0;
      }
      
      .logo-wordmark {
        display: flex;
        align-items: center;
        font-weight: 700;
        letter-spacing: -0.5px;
        white-space: nowrap;
      }
      
      .logo-text-ascend {
        color: #9CA3AF;
      }
      
      .logo-text-draft {
        color: #FFFFFF;
      }
      
      .logo-icon-only {
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      
      .logo-text-only {
        display: inline-flex;
        align-items: center;
      }
      
      .logo-text-full {
        white-space: nowrap;
      }
      
      /* Glow effect on hover */
      .logo-glow:hover .logo-icon rect {
        filter: drop-shadow(0 0 8px rgba(0, 188, 212, 0.5));
      }
      
      .logo-glow:hover .logo-icon path {
        filter: drop-shadow(0 0 6px rgba(0, 188, 212, 0.6));
      }
      
      /* Animation for loading state */
      .logo-animate .logo-icon {
        animation: logo-pulse 2s ease-in-out infinite;
      }
      
      @keyframes logo-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.7; }
      }
      
      /* Responsive adjustments */
      @media (max-width: 576px) {
        .logo-wordmark {
          display: none;
        }
        
        .logo.logo-responsive .logo-wordmark {
          display: none;
        }
      }
    `;
    document.head.appendChild(styles);
  }

  // Auto-inject styles on load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', injectLogoStyles);
} else {
  injectLogoStyles();
}

// ========================================================================
// EXPORTS
// ========================================================================

// Export for ES modules
export default Logo;

// Also export individual functions
export { Logo, renderLogo, LOGO_COLORS, LOGO_SIZES };

// Also keep global for backward compatibility (in non-module scripts)
if (typeof window !== 'undefined') {
  window.AscendLogo = Logo;
  window.Logo = Logo;
  window.renderLogo = renderLogo;
}
